import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useHookFormMask } from "use-mask-input";
import { toast } from "react-toastify";

import "./forms.css";
import Button from "../button/button";
import { PHONE_REGEX } from "../../utils/pagesMapper";
import ErrorMessage from "../forms/errorMessage/errorMessage";
import LandmarkBlue from "../../assets/icons/landmark-blue.svg";
import PaletteIcon from "../../assets/icons/palette.svg";
import CheckedCircle from "../../assets/icons/checked-circle.svg";
import { useInstitution } from "../../context/InstitutionContext";
import TriangleExclamation from "../../assets/icons/triangle-exclamation.svg";
import {
  createInstitution,
  readByUser,
  updateInstitution
} from "../../services/academetrics";
import {
  getInstitutionTheme,
  setInstitutionTheme,
  saveInstitutionLogo,
  removeInstitutionLogo,
  removeThemePreference,
  PREFERENCE_KEYS,
  DEFAULT_THEME
} from "../../services/preferences";

const CEP_REGEX = /^\d{5}-\d{3}$/;

// Formata um valor decimal para o formato de moeda brasileira (R$ X.XXX,XX)
// Formato esperado pela máscara: "R$ 1.234,56"
const formatToBrazilianCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "";
  
  // Se já estiver formatado com R$, retorna como está
  if (typeof value === "string" && value.includes("R$")) return value;
  
  // Converte para número
  const numValue = typeof value === "string" ? parseFloat(value) : value;
  
  if (isNaN(numValue)) return "";
  
  // Formata manualmente para o formato esperado pela máscara: "R$ X.XXX,XX"
  const formatted = numValue.toFixed(2).replace(".", ",");
  const parts = formatted.split(",");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `R$ ${parts.join(",")}`;
};

// Normaliza o payload da instituição garantindo que os campos opcionais
// vazios não sejam enviados como "" — string vazia não desserializa para
// int?/decimal? no backend e geraria um 400 num campo que é opcional.
const buildInstitutionPayload = (data, extra = {}) => {
  const payload = { ...data, ...extra };

  // employeesCount / averageStudents -> int? no backend
  ["employeesCount", "averageStudents"].forEach((field) => {
    const value = payload[field];
    if (value === "" || value === null || value === undefined) {
      delete payload[field];
    } else {
      payload[field] = Number(value);
    }
  });

  // complement -> string? ; averageMonthlyTicket -> decimal? (parseado no backend)
  ["complement", "averageMonthlyTicket"].forEach((field) => {
    const value = payload[field];
    if (value === null || value === undefined) {
      delete payload[field];
      return;
    }
    const trimmed = String(value).trim();
    const ticketWithoutDigits =
      field === "averageMonthlyTicket" && !/\d/.test(trimmed);
    if (trimmed === "" || ticketWithoutDigits) {
      delete payload[field];
    }
  });

  return payload;
};

const CreateInstitution = () => {
  const { institution, setInstitution } = useInstitution();

  // Estado para o tema (personalização visual)
  const [themeData, setThemeData] = useState({
    primaryColor: DEFAULT_THEME.primaryColor,
    secondaryColor: DEFAULT_THEME.secondaryColor
  });
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null); // Arquivo do logo para upload
  
  // Rastreia quais campos de tema foram modificados pelo usuário
  const [modifiedThemeFields, setModifiedThemeFields] = useState({
    primaryColor: false,
    secondaryColor: false,
    logo: false
  });
  
  // Rastreia se as cores foram resetadas para o padrão (para deletar do backend)
  const [colorsResetToDefault, setColorsResetToDefault] = useState(false);
  
  // Estado para o modal de aplicando configurações
  const [showApplyingModal, setShowApplyingModal] = useState(false);

  const institutionFormFormatted = () => {
    return {
      name: institution?.name,
      institutionType: institution?.institutionType,
      zipCode: institution?.zipCode?.replace(/^(\d{5})(\d{3})$/, "$1-$2"),
      address: institution?.address,
      number: institution?.number,
      complement: institution?.complement,
      city: institution?.city,
      state: institution?.state,
      country: institution?.country,
      email: institution?.email,
      phone: institution?.phone,
      // Novos campos de dados operacionais
      employeesCount: institution?.employeesCount || "",
      averageStudents: institution?.averageStudents || "",
      averageMonthlyTicket: formatToBrazilianCurrency(institution?.averageMonthlyTicket)
    };
  };

  const {
    register,
    setValue,
    handleSubmit,
    formState: { errors }
  } = useForm({ values: institutionFormFormatted() });

  const registerWithMask = useHookFormMask(register);

  const [ufs, setUfs] = useState([]);
  const [loading, setLoading] = useState(false);

  const getUfs = () => {
    fetch("https://servicodados.ibge.gov.br/api/v1/localidades/estados")
      .then((response) => response.json())
      .then((data) => setUfs(data));
  };

  const getCepInfo = (zipCode) => {
    if (!CEP_REGEX.test(zipCode)) return;

    fetch(`https://viacep.com.br/ws/${zipCode}/json/`)
      .then((response) => response.json())
      .then((data) => {
        setValue("address", data.logradouro);
        setValue("city", data.localidade);
        setValue("state", data.uf);
        setValue("country", "Brasil");
      });
  };

  // Carrega tema da instituição ao montar
  useEffect(() => {
    const loadTheme = async () => {
      if (institution?.id) {
        try {
          const theme = await getInstitutionTheme();
          setThemeData({
            primaryColor: theme.primaryColor || DEFAULT_THEME.primaryColor,
            secondaryColor: theme.secondaryColor || DEFAULT_THEME.secondaryColor
          });
          // Logo é carregado separadamente (URL retornada pelo backend)
          if (theme.logo) {
            setLogoPreview(theme.logo);
          }
        } catch (error) {
          console.error("Erro ao carregar tema:", error);
        }
      }
    };

    loadTheme();
  }, [institution?.id]);

  // Força o valor do campo averageMonthlyTicket com a formatação correta
  // Necessário porque campos com máscara de currency não funcionam bem com valores iniciais
  // Usa setTimeout para garantir que a máscara já esteja inicializada
  useEffect(() => {
    if (institution?.averageMonthlyTicket !== null && institution?.averageMonthlyTicket !== undefined) {
      const formattedValue = formatToBrazilianCurrency(institution.averageMonthlyTicket);
      // Pequeno delay para garantir que o componente e a máscara estejam prontos
      const timer = setTimeout(() => {
        setValue("averageMonthlyTicket", formattedValue, { shouldValidate: false });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [institution?.averageMonthlyTicket, setValue]);

  useEffect(() => {
    getUfs();
  }, []);

  const onSubmit = (data) => {
    institution?.id ? handleUpdate(data) : handleRegister(data);
  };

  // Prepara apenas os campos de cores que foram modificados (logo é tratado separadamente)
  const getModifiedThemeData = () => {
    const modifiedData = {};
    
    if (modifiedThemeFields.primaryColor) {
      modifiedData.primaryColor = themeData.primaryColor;
    }
    if (modifiedThemeFields.secondaryColor) {
      modifiedData.secondaryColor = themeData.secondaryColor;
    }
    
    return modifiedData;
  };

  // Deleta as preferências de cores (volta ao padrão no backend)
  const handleResetColorsOnBackend = async () => {
    try {
      await Promise.all([
        removeThemePreference(PREFERENCE_KEYS.PRIMARY_COLOR).catch(() => {}),
        removeThemePreference(PREFERENCE_KEYS.SECONDARY_COLOR).catch(() => {})
      ]);
    } catch (error) {
      console.error("Erro ao resetar cores:", error);
    }
  };

  // Mostra modal e faz refresh após delay
  const showModalAndRefresh = () => {
    setShowApplyingModal(true);
    setTimeout(() => {
      window.location.reload();
    }, 1500);
  };

  const handleRegister = async (data) => {
    setLoading(true);

    try {
      // Cria a instituição (o backend devolve o id da instituição criada)
      const payload = buildInstitutionPayload(data);
      const newInstitutionId = await createInstitution(payload);

      const user = await readByUser();

      // Para MantenedoraAdmin, readByUser não retorna a instituição recém-criada
      // (o JWT tem institutionId vazio). Nesse caso, monta o contexto a partir
      // do payload + id devolvido pelo create, garantindo que o contexto tenha
      // um id válido — sem isso, chamadas como listFiles enviam o header
      // institutionId vazio e retornam 400.
      const createdInstitution =
        user?.institution?.id
          ? user.institution
          : newInstitutionId
            ? { id: newInstitutionId, ...payload }
            : null;

      if (createdInstitution?.id) {
        setInstitution(createdInstitution);
      }

      // Prepara as promises de preferências para executar em paralelo
      const preferencesPromises = [];

      // Se as cores foram resetadas para o padrão, não precisa salvar nada (instituição nova)
      // Caso contrário, salva as cores modificadas
      if (!colorsResetToDefault) {
        const modifiedTheme = getModifiedThemeData();
        if (Object.keys(modifiedTheme).length > 0) {
          preferencesPromises.push(setInstitutionTheme(modifiedTheme));
        }
      }

      // Salva o logo se foi modificado (usa o id da instituição recém-criada)
      if (createdInstitution?.id && modifiedThemeFields.logo && logoFile) {
        preferencesPromises.push(
          saveInstitutionLogo(logoFile, createdInstitution.id)
        );
      }

      // Aguarda todas as preferências serem salvas
      if (preferencesPromises.length > 0) {
        await Promise.all(preferencesPromises);
      }

      // Reseta o estado de reset
      setColorsResetToDefault(false);

      setLoading(false);
      
      // Mostra modal e faz refresh somente após todas as operações
      showModalAndRefresh();
    } catch (error) {
      console.log("@error", error);
      toast.error("Erro ao criar instituição.");
      setLoading(false);
    }
  };

  const handleUpdate = async (data) => {
    setLoading(true);

    try {
      // Atualiza dados da instituição
      await updateInstitution(
        buildInstitutionPayload(data, { institutionId: institution?.id })
      );

      const user = await readByUser();
      setInstitution(user.institution);

      // Prepara as promises de preferências para executar em paralelo
      const preferencesPromises = [];

      // Se as cores foram resetadas para o padrão, deleta as preferências do backend
      if (colorsResetToDefault) {
        preferencesPromises.push(handleResetColorsOnBackend());
      } else {
        // Caso contrário, salva as cores modificadas
        const modifiedTheme = getModifiedThemeData();
        if (Object.keys(modifiedTheme).length > 0) {
          preferencesPromises.push(setInstitutionTheme(modifiedTheme));
        }
      }

      // Salva o logo se foi modificado
      if (modifiedThemeFields.logo) {
        if (logoFile) {
          preferencesPromises.push(saveInstitutionLogo(logoFile, institution?.id));
        } else if (logoPreview === null) {
          // Logo foi removido
          preferencesPromises.push(removeInstitutionLogo());
        }
      }

      // Aguarda todas as preferências serem salvas
      if (preferencesPromises.length > 0) {
        await Promise.all(preferencesPromises);
      }

      // Reseta o estado de reset
      setColorsResetToDefault(false);

      setLoading(false);
      
      // Mostra modal e faz refresh somente após todas as operações
      showModalAndRefresh();
    } catch (error) {
      console.log("@error", error);
      toast.error("Erro ao atualizar instituição.");
      setLoading(false);
    }
  };

  const handleColorChange = (colorType, value) => {
    setThemeData((prev) => ({
      ...prev,
      [colorType]: value
    }));
    // Marca o campo como modificado
    setModifiedThemeFields((prev) => ({
      ...prev,
      [colorType]: true
    }));
    // Se o usuário modificar uma cor manualmente, cancela o reset para padrão
    setColorsResetToDefault(false);
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validação de tamanho (máximo 2MB)
      if (file.size > 2 * 1024 * 1024) {
        toast.error("O logo deve ter no máximo 2MB.");
        return;
      }

      // Validação de tipo (apenas JPG e PNG)
      const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
      if (!allowedTypes.includes(file.type)) {
        toast.error("O arquivo deve ser JPG ou PNG.");
        return;
      }

      // Armazena o arquivo para upload
      setLogoFile(file);
      
      // Cria preview usando URL.createObjectURL
      const previewUrl = URL.createObjectURL(file);
      setLogoPreview(previewUrl);
      
      // Marca o campo como modificado
      setModifiedThemeFields((prev) => ({
        ...prev,
        logo: true
      }));
    }
  };

  const handleRemoveLogo = () => {
    // Revoga a URL do preview se existir
    if (logoPreview && logoPreview.startsWith("blob:")) {
      URL.revokeObjectURL(logoPreview);
    }
    setLogoPreview(null);
    setLogoFile(null);
    setModifiedThemeFields((prev) => ({
      ...prev,
      logo: true // Marca como modificado para remover no backend se necessário
    }));
  };

  const handleResetColors = () => {
    setThemeData((prev) => ({
      ...prev,
      primaryColor: DEFAULT_THEME.primaryColor,
      secondaryColor: DEFAULT_THEME.secondaryColor
    }));
    // Marca como modificado para que o reset seja salvo
    setModifiedThemeFields((prev) => ({
      ...prev,
      primaryColor: true,
      secondaryColor: true
    }));
    // Marca que as cores foram resetadas para o padrão (para deletar do backend)
    setColorsResetToDefault(true);
    toast.info("Cores restauradas para o padrão.");
  };

  return (
    <div className="mainContentSpace">
      <div className="institution-tab">
        <img src={LandmarkBlue} alt="Instituição" />
        <span>Instituição</span>
      </div>

      <div className="institution-form">
        <div className="warning-message">
          <img src={TriangleExclamation} alt="Warning" />
          <span>
            É necessário configurar a sua instituição antes de utilizar o
            sistema pela primeira vez.
          </span>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          {/* SEÇÃO: DADOS DA INSTITUIÇÃO */}
          <div className="form-section">
            <div className="form-section-header">
              <img src={LandmarkBlue} alt="Dados" />
              <h3>Dados da Instituição</h3>
            </div>

            <fieldset>
              <legend>Informações Básicas</legend>

              <div className="form-row" style={{ alignItems: "flex-start" }}>
                <div className="form-group">
                  <input
                    id="name"
                    name="name"
                    placeholder="Nome da Instituição"
                    {...register("name", { required: "O nome é obrigatório." })}
                  />
                  <ErrorMessage message={errors.name?.message} />
                </div>

                <div className="form-group">
                  <select
                    id="institutionType"
                    name="institutionType"
                    {...register("institutionType", {
                      required: "O tipo é obrigatório."
                    })}
                  >
                    <option value="University">Universidade</option>
                    <option value="School">Escola</option>
                    <option value="Other">Outro</option>
                  </select>
                  <ErrorMessage message={errors.institutionType?.message} />
                </div>
              </div>
            </fieldset>

            <fieldset>
              <legend>Endereço</legend>

              <div className="form-row">
                <div className="form-group">
                  <input
                    id="zipCode"
                    name="zipCode"
                    placeholder="CEP"
                    {...registerWithMask("zipCode", ["99999-999"], {
                      required: "O CEP é obrigatório.",
                      pattern: {
                        value: CEP_REGEX,
                        message: "O CEP é inválido."
                      },
                      onBlur: (e) => getCepInfo(e.target.value)
                    })}
                  />
                  <ErrorMessage message={errors.zipCode?.message} />
                </div>

                <div className="form-group">
                  <input
                    id="address"
                    name="address"
                    placeholder="Rua"
                    {...register("address", { required: "A rua é obrigatório." })}
                  />
                  <ErrorMessage message={errors.address?.message} />
                </div>

                <div className="form-group">
                  <input
                    id="number"
                    name="number"
                    placeholder="Nº"
                    {...register("number", {
                      required: "O número é obrigatório."
                    })}
                  />
                  <ErrorMessage message={errors.number?.message} />
                </div>

                <div className="form-group">
                  <input
                    id="complement"
                    name="complement"
                    placeholder="Complemento"
                    {...register("complement")}
                  />
                  <ErrorMessage message={errors.complement?.message} />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <input
                    id="city"
                    name="city"
                    {...register("city", { required: "A cidade é obrigatória." })}
                    placeholder="Cidade"
                  />
                  <ErrorMessage message={errors.city?.message} />
                </div>

                <div className="form-group">
                  <select
                    id="state"
                    name="state"
                    {...register("state", { required: "A UF é obrigatória." })}
                  >
                    <option disabled>UF</option>
                    {ufs.map((uf, index) => (
                      <option key={uf.sigla} value={uf.sigla}>
                        {uf.nome}
                      </option>
                    ))}
                  </select>
                  <ErrorMessage message={errors.state?.message} />
                </div>

                <div className="form-group">
                  <select
                    id="country"
                    name="country"
                    {...register("country", {
                      required: "O país é obrigatório."
                    })}
                  >
                    <option value="Brasil">Brasil</option>
                  </select>
                  <ErrorMessage message={errors.country?.message} />
                </div>
              </div>
            </fieldset>

            <fieldset>
              <legend>Contato</legend>

              <div className="form-row">
                <div className="form-group">
                  <input
                    id="email"
                    name="email"
                    placeholder="E-mail"
                    {...register("email", {
                      required: "O e-mail é obrigatório.",
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: "O e-mail é inválido."
                      }
                    })}
                  />
                  <ErrorMessage message={errors.email?.message} />
                </div>

                <div className="form-group">
                  <input
                    id="phone"
                    name="phone"
                    placeholder="Telefone"
                    {...registerWithMask("phone", ["(99) 99999-9999"], {
                      required: "O telefone é obrigatório.",
                      pattern: {
                        value: PHONE_REGEX,
                        message: "O telefone é inválido."
                      }
                    })}
                  />
                  <ErrorMessage message={errors.phone?.message} />
                </div>
              </div>
            </fieldset>

            <fieldset>
              <legend>Dados Operacionais</legend>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="employeesCount">Quantidade de Funcionários</label>
                  <input
                    id="employeesCount"
                    name="employeesCount"
                    type="number"
                    min="1"
                    placeholder="Ex: 50"
                    {...register("employeesCount", {
                      min: { value: 1, message: "Mínimo de 1 funcionário." }
                    })}
                  />
                  <ErrorMessage message={errors.employeesCount?.message} />
                </div>

                <div className="form-group">
                  <label htmlFor="averageStudents">Quantidade Média de Alunos</label>
                  <input
                    id="averageStudents"
                    name="averageStudents"
                    type="number"
                    min="1"
                    placeholder="Ex: 500"
                    {...register("averageStudents", {
                      min: { value: 1, message: "Mínimo de 1 aluno." }
                    })}
                  />
                  <ErrorMessage message={errors.averageStudents?.message} />
                </div>

                <div className="form-group">
                  <label htmlFor="averageMonthlyTicket">Ticket Médio Mensal</label>
                  <input
                    id="averageMonthlyTicket"
                    name="averageMonthlyTicket"
                    type="text"
                    placeholder="R$ 0,00"
                    {...registerWithMask("averageMonthlyTicket", "currency", {
                      prefix: "R$ ",
                      radixPoint: ",",
                      groupSeparator: ".",
                      digits: 2,
                      digitsOptional: false,
                      allowMinus: false
                    })}
                  />
                  <ErrorMessage message={errors.averageMonthlyTicket?.message} />
                </div>
              </div>
            </fieldset>
          </div>

          {/* SEÇÃO: PERSONALIZAÇÃO VISUAL */}
          <div className="form-section">
            <div className="form-section-header">
              <img src={PaletteIcon} alt="Personalização" />
              <h3>Personalização Visual</h3>
            </div>

            <fieldset>
              <legend>Logo da Instituição</legend>

              <div className="logo-upload-container">
                {logoPreview ? (
                  <div className="logo-preview">
                    <img src={logoPreview} alt="Logo Preview" />
                    <button
                      type="button"
                      className="remove-logo-btn"
                      onClick={handleRemoveLogo}
                    >
                      Remover
                    </button>
                  </div>
                ) : (
                  <div className="logo-upload-placeholder">
                    <input
                      type="file"
                      id="logo"
                      accept="image/png, image/jpeg, image/jpg"
                      onChange={handleLogoChange}
                    />
                    <label htmlFor="logo">
                      <span>Clique para enviar o logo</span>
                      <small>PNG ou JPG (máx. 2MB)</small>
                    </label>
                  </div>
                )}
              </div>
            </fieldset>

            <fieldset>
              <legend>Cores da Interface</legend>

              <div className="form-row color-picker-row">
                <div className="form-group color-picker-group">
                  <label htmlFor="primaryColor">Cor Primária</label>
                  <div className="color-input-wrapper">
                    <input
                      type="color"
                      id="primaryColor"
                      value={themeData.primaryColor}
                      onChange={(e) => handleColorChange("primaryColor", e.target.value)}
                    />
                    <span className="color-value">{themeData.primaryColor}</span>
                  </div>
                  <small className="color-hint">
                    Usada em botões, links e destaques
                  </small>
                </div>

                <div className="form-group color-picker-group">
                  <label htmlFor="secondaryColor">Cor Secundária</label>
                  <div className="color-input-wrapper">
                    <input
                      type="color"
                      id="secondaryColor"
                      value={themeData.secondaryColor}
                      onChange={(e) => handleColorChange("secondaryColor", e.target.value)}
                    />
                    <span className="color-value">{themeData.secondaryColor}</span>
                  </div>
                  <small className="color-hint">
                    Usada em elementos secundários e hover
                  </small>
                </div>

                <div className="color-preview">
                  <span className="preview-label">Pré-visualização</span>
                  <div className="preview-buttons">
                    <button
                      type="button"
                      style={{ backgroundColor: themeData.primaryColor }}
                    >
                      Primária
                    </button>
                    <button
                      type="button"
                      style={{ backgroundColor: themeData.secondaryColor }}
                    >
                      Secundária
                    </button>
                  </div>
                </div>
              </div>

              <div className="color-reset-container">
                <button
                  type="button"
                  className="reset-colors-btn"
                  onClick={handleResetColors}
                >
                  Restaurar cores padrão
                </button>
              </div>
            </fieldset>
          </div>

          <div className="form-actions">
            <Button
              text={institution ? "Salvar Alterações" : "Criar Instituição"}
              variation="primary"
              size="big"
              icon={CheckedCircle}
              loading={loading}
            />
          </div>
        </form>
      </div>

      {/* Modal de Aplicando Configurações */}
      {showApplyingModal && (
        <div className="applying-modal-overlay">
          <div className="applying-modal">
            <div className="applying-spinner"></div>
            <span>Aplicando configurações...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateInstitution;
